<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div class="card shadow-sm">
            <div class="card-body">
              <!-- Header with title and New Order button -->
              <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <h1 class="h4 mb-0">Devices</h1>
                <div class="d-flex gap-2">
                  <button class="btn btn-primary btn-sm" @click="goCreateOrder">
                    New Device
                  </button>
                  <button class="btn btn-outline-primary btn-sm" @click="$router.push({ name: 'BatchCreate' })">
                    Batch Create
                  </button>
                </div>
              </div>
              <p class="help mb-3">
                Listing all devices available to you.
              </p>

              <!-- Loading spinner shown while fetching orders -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border" role="status"></div>
              </div>

              <div v-else>
                <!-- Error message displayed if API call fails -->
                <div v-if="error" class="alert alert-danger py-2">
                  {{ error }}
                </div>

                <!-- Message when API returns no orders -->
                <div v-if="orders.length === 0" class="text-muted">
                  No devices found.
                </div>

                <!-- Main content: filters and orders table -->
                <div v-else>
                  <!-- Filter controls -->
                  <div class="row g-2 mb-3">
                    <!-- Status dropdown filter -->
                    <div class="col-md-3">
                      <label class="form-label small mb-1 d-flex justify-content-between">
                        <span>Status</span>
                        <span class="text-muted">{{ filteredOrders.length }} matching</span>
                      </label>
                      <select v-model="statusFilter" class="form-select form-select-sm">
                        <option value="">All statuses</option>
                        <option v-for="opt in statusOptions" :key="opt" :value="opt">
                          {{ opt }}
                        </option>
                      </select>
                    </div>

                    <!-- Device type dropdown filter -->
                    <div class="col-md-3">
                      <label class="form-label small mb-1">Device</label>
                      <select v-model="deviceFilter" class="form-select form-select-sm">
                        <option value="">All devices</option>
                        <option v-for="dev in deviceOptions" :key="dev" :value="dev">
                          {{ dev }}
                        </option>
                      </select>
                    </div>

                    <!-- Serial number search input -->
                    <div class="col-md-3">
                      <label class="form-label small mb-1">Serial search</label>
                      <input
                        ref="serialInput"
                        v-model="serialSearch"
                        type="text"
                        class="form-control form-control-sm"
                        placeholder="Search by serial..."
                      />
                    </div>

                    <!-- Shipping status dropdown filter -->
                    <div class="col-md-3">
                      <label class="form-label small mb-1">Shipping Status</label>
                      <select v-model="shippingStatusFilter" class="form-select form-select-sm">
                        <option value="">All shipping</option>
                        <option value="LABEL_CREATED">LABEL_CREATED</option>
                        <option value="IN_TRANSIT">IN_TRANSIT</option>
                        <option value="OUT_FOR_DELIVERY">OUT_FOR_DELIVERY</option>
                        <option value="DELIVERED">DELIVERED</option>
                        <option value="EXCEPTION">EXCEPTION</option>
                      </select>
                    </div>
                  </div>

                  <!-- Bulk action bar -->
                  <div v-if="hasSelection" class="alert alert-info d-flex align-items-center gap-2 py-2 mb-3 flex-wrap">
                    <span>{{ selectedOrders.length }} selected</span>
                    <select v-model="bulkTargetStatus" class="form-select form-select-sm w-auto">
                      <option value="">Move to...</option>
                      <option v-for="opt in statusOptions" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                    <button class="btn btn-primary btn-sm" :disabled="!bulkTargetStatus || bulkLoading" @click="applyBulkStatus">
                      <span v-if="bulkLoading" class="spinner-border spinner-border-sm me-1"></span>
                      Apply
                    </button>
                    <button class="btn btn-outline-secondary btn-sm" @click="batchPrintSerials">Print Serial Labels</button>
                    <button class="btn btn-outline-secondary btn-sm" @click="batchPrintSheets">Print Order Sheets</button>
                    <button
                      v-if="isSupervisor && allSelectedComplete"
                      class="btn btn-outline-secondary btn-sm"
                      @click="ebayBatchModal.visible = true"
                    >
                      Add eBay Order ID
                    </button>
                    <button class="btn btn-outline-secondary btn-sm" @click="selectedOrders = []">Clear</button>
                    <span v-if="bulkResult" class="small ms-2">
                      {{ bulkResult }}
                    </span>
                  </div>

                  <!-- eBay batch import modal -->
                  <InputModal
                    :visible="ebayBatchModal.visible"
                    title="Add eBay Order ID"
                    message="Enter the eBay order ID to assign to the selected orders."
                    input-label="eBay Order ID"
                    placeholder="e.g. 12-34567-89012"
                    :required="true"
                    confirm-text="Import"
                    @confirm="batchEbayImport"
                    @cancel="ebayBatchModal.visible = false"
                  />

                  <!-- Message when filters return no results -->
                  <div v-if="filteredOrders.length === 0" class="text-muted">
                    No devices match the current filters.
                  </div>

                  <!-- Orders table with responsive wrapper -->
                  <div v-else class="table-responsive">
                    <table class="table table-sm align-middle">
                      <thead class="table-light">
                        <tr>
                          <th style="width: 40px;">
                            <input type="checkbox" v-model="selectAllChecked" />
                          </th>
                          <th>ID</th>
                          <th>Device</th>
                          <th>Serial</th>
                          <th>Assignee</th>
                          <th>Template</th>
                          <th>Started</th>
                          <th>Status</th>
                          <th class="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- Loop through filtered orders and render each row -->
                        <tr v-for="order in filteredOrders" :key="order.order_id">
                          <td>
                            <input type="checkbox" :value="order.order_id" v-model="selectedOrders" />
                          </td>
                          <td>{{ order.order_id }}</td>
                          <td>{{ order.device_commonname }}</td>
                          <td>{{ order.serial_number }}</td>

                          <!-- Assignee column: dropdown for supervisors, read-only for others -->
                          <td>
                            <!-- Supervisors/Admin can assign orders to technicians -->
                            <div v-if="isSupervisor">
                              <select
                                v-model="order.localAssigneeId"
                                class="form-select form-select-sm"
                                @change="onAssigneeChange(order)"
                                :disabled="order.savingAssignee"
                              >
                                <option :value="null">Unassigned</option>
                                <option v-for="tech in technicianOptions" :key="tech.id" :value="tech.id">
                                  {{ tech.name }}
                                </option>
                              </select>
                              <!-- Show loading state while updating -->
                              <small v-if="order.savingAssignee" class="text-muted">
                                Updating...
                              </small>
                              <!-- Show error if assignee update failed -->
                              <small v-if="order.assigneeError" class="text-danger">
                                {{ order.assigneeError }}
                              </small>
                            </div>

                            <!-- Non-supervisors see read-only assignee name -->
                            <div v-else>
                              {{ order.user_name || "Unassigned" }}
                            </div>
                          </td>

                          <td>{{ order.template_name }}</td>
                          <td>{{ formatDate(order.started_at) }}</td>
                          <td>{{ order.order_status }}</td>
                          <!-- Action button to view order details -->
                          <td class="text-end">
                            <button
                              class="btn btn-outline-secondary btn-sm"
                              @click="goDetail(order.order_id)"
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";
import InputModal from "./InputModal.vue";

export default {
  name: "OrdersList",
  components: { InputModal },
  data() {
    return {
      // Data properties
      orders: [],          // Array of orders fetched from API
      loading: false,      // Loading state for API calls
      error: null,         // Error message from failed API calls
      api: new APIService(),

      // Filter state
      statusFilter: "",    // Current status filter value
      deviceFilter: "",    // Current device filter value
      serialSearch: "",    // Current serial number search term
      shippingStatusFilter: "",  // Current shipping status filter

      // Bulk selection state
      selectedOrders: [],
      bulkTargetStatus: "",
      bulkLoading: false,
      bulkResult: null,

      // eBay batch modal state
      ebayBatchModal: { visible: false },
    };
  },

  // Focus serial input field when component mounts and restore filters from URL
  mounted() {
    // Restore filters from URL query params
    this.restoreFiltersFromUrl();

    this.$nextTick(() => {
      const el = this.$refs.serialInput;
      if (el) el.focus();
    });
  },

  watch: {
    // Sync filter changes to URL
    statusFilter() {
      this.updateUrlQueryParams();
    },
    deviceFilter() {
      this.updateUrlQueryParams();
    },
    serialSearch() {
      this.updateUrlQueryParams();
    },
    shippingStatusFilter() {
      this.updateUrlQueryParams();
    },
  },

  computed: {
    // Get current user's role from localStorage
    roleLabel() {
      try {
        const raw = localStorage.getItem("currentUser");
        if (!raw) return "";
        const user = JSON.parse(raw);
        return user.role || "";
      } catch {
        return "";
      }
    },

    // Check if current user is a supervisor or admin
    isSupervisor() {
      const role = (this.roleLabel || "").toLowerCase();
      return role === "supervisor" || role === "admin";
    },

    // Build list of available order statuses
    statusOptions() {
      const base = ["INTAKE", "REFURBISHING", "QA", "HOLD", "COMPLETE", "CANCELLED"];
      // Only supervisors/admins can see SOLD status
      if (this.isSupervisor) {
        base.push("SOLD");
      }
      return base;
    },

    // Extract unique device types from all orders
    deviceOptions() {
      const names = this.orders
        .map((o) => o.device_commonname)
        .filter(Boolean);
      return [...new Set(names)].sort();
    },

    // Build list of technicians from orders for assignment dropdown
    technicianOptions() {
      const map = new Map();
      for (const o of this.orders) {
        if (o.user != null) {
          const name = o.user_name || `User #${o.user}`;
          if (!map.has(o.user)) {
            map.set(o.user, name);
          }
        }
      }
      return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    },

    hasSelection() {
      return this.selectedOrders.length > 0;
    },

    allSelectedComplete() {
      if (!this.hasSelection) return false;
      return this.selectedOrders.every((id) => {
        const order = this.orders.find((o) => o.order_id === id);
        return order && order.order_status === "COMPLETE";
      });
    },

    selectAllChecked: {
      get() {
        return this.filteredOrders.length > 0 && this.selectedOrders.length === this.filteredOrders.length;
      },
      set(val) {
        this.selectedOrders = val ? this.filteredOrders.map((o) => o.order_id) : [];
      },
    },

    // Filter orders based on status, device, and serial number search
    filteredOrders() {
      const status = this.statusFilter;
      const device = this.deviceFilter;
      const query = this.serialSearch.trim().toLowerCase();
      const shippingStatus = this.shippingStatusFilter;

      return this.orders.filter((order) => {
        // By default, exclude SOLD and CANCELLED orders; show them if explicitly selected
        const matchesStatus = !status
          ? (order.order_status !== "SOLD" && order.order_status !== "CANCELLED")
          : order.order_status === status;

        const matchesDevice =
          !device || order.device_commonname === device;

        const serial = (order.serial_number || "").toLowerCase();
        const matchesSerial =
          !query || serial.includes(query);

        const matchesShipping =
          !shippingStatus || order.shipping_status === shippingStatus;

        return matchesStatus && matchesDevice && matchesSerial && matchesShipping;
      });
    },
  },

  // Fetch orders when component is created
  created() {
    this.fetchOrders();
  },

  methods: {
    // Fetch all orders from API and populate local data
    async fetchOrders() {
      this.loading = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getOrders(accessToken);

        // Add local fields for UI state management
        this.orders = (res.data || []).map((o) => ({
          ...o,
          localAssigneeId: o.user ?? null,  // Track dropdown selection
          savingAssignee: false,            // Track save state
          assigneeError: null,              // Track per-row assignee errors
        }));
      } catch (err) {
        this.error = "Unable to load orders.";
      } finally {
        this.loading = false;

        // Refocus serial input after loading completes
        this.$nextTick(() => {
          const el = this.$refs.serialInput;
          if (el) el.focus();
        });
      }
    },

    // Handle supervisor reassigning an order to a different technician
    async onAssigneeChange(order) {
      if (!this.isSupervisor) return;

      const newUserId = order.localAssigneeId || null;
      const previousUserId = order.user;

      order.savingAssignee = true;
      order.assigneeError = null;

      try {
        const accessToken = localStorage.getItem("accessToken");
        // Update order assignment via API
        await this.api.updateOrderStatus(
          order.order_id,
          { user: newUserId },
          accessToken
        );

        // Update local order object with new assignee info
        order.user = newUserId;
        const tech = this.technicianOptions.find((t) => t.id === newUserId);
        order.user_name = tech ? tech.name : (newUserId ? `User #${newUserId}` : null);
      } catch (e) {
        console.error("Unable to update assignee:", e);
        order.assigneeError = "Failed to update";
        // Revert dropdown to previous value on error
        order.localAssigneeId = previousUserId ?? null;
      } finally {
        order.savingAssignee = false;
      }
    },

    // Navigate to order detail page
    goDetail(orderId) {
      const ids = this.filteredOrders.map((o) => o.order_id);
      sessionStorage.setItem("orderNavList", JSON.stringify(ids));
      this.$router.push({ name: "OrderDetail", params: { id: orderId } });
    },

    // Navigate to create new order page
    goCreateOrder() {
      this.$router.push({ name: "CreateOrder" });
    },

    async applyBulkStatus() {
      if (!this.bulkTargetStatus || this.selectedOrders.length === 0) return;
      this.bulkLoading = true;
      this.bulkResult = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.bulkUpdateStatus(
          { order_ids: this.selectedOrders, new_status: this.bulkTargetStatus },
          accessToken
        );
        const data = res.data;
        const updated = (data.updated || []).length;
        const errors = (data.errors || []).length;
        this.bulkResult = `${updated} updated, ${errors} failed`;
        this.selectedOrders = [];
        this.bulkTargetStatus = "";
        await this.fetchOrders();
      } catch {
        this.bulkResult = "Bulk update failed.";
      } finally {
        this.bulkLoading = false;
      }
    },

    batchPrintSerials() {
      const serials = this.selectedOrders
        .map((id) => this.orders.find((o) => o.order_id === id))
        .filter((o) => o && o.serial_number)
        .map((o) => o.serial_number.trim());

      if (serials.length === 0) {
        alert("None of the selected orders have a serial number.");
        return;
      }

      const labelHtml = serials.map((serial, i) => `
        <div class="label-wrapper" ${i < serials.length - 1 ? 'style="page-break-after: always;"' : ''}>
          <svg id="barcode-${i}"></svg>
          <div class="serial-text">${serial}</div>
        </div>
      `).join("");

      const barcodeScripts = serials.map((serial, i) => `
        JsBarcode("#barcode-${i}", ${JSON.stringify(serial)}, {
          format: "CODE128",
          displayValue: false,
          margin: 0,
          width: 1.2,
          height: 40
        });
      `).join("");

      const printWindow = window.open("", "", "width=300,height=200");
      printWindow.document.write(`
        <html>
          <head>
            <title>Serial Labels</title>
            <style>
              @page {
                size: 2in 1in;
                margin: 0;
              }
              html, body {
                margin: 0;
                padding: 0;
              }
              .label-wrapper {
                width: 2in;
                height: 1in;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                overflow: hidden;
                font-family: Arial, sans-serif;
              }
              .label-wrapper svg {
                width: 1.8in;
                height: 0.6in;
              }
              .serial-text {
                font-size: 0.14in;
                line-height: 1;
                margin-top: 0.03in;
                text-align: center;
                white-space: nowrap;
              }
            </style>
          </head>
          <body>
            ${labelHtml}
            <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
            <script>
              window.addEventListener('load', function () {
                ${barcodeScripts}
                setTimeout(() => window.print(), 200);
              });
            <\/script>
          </body>
        </html>
      `);
      printWindow.document.close();
    },

    batchPrintSheets() {
      const ids = this.selectedOrders.join(",");
      const route = this.$router.resolve({ name: "BatchPrintOrders", query: { ids } });
      window.open(route.href, "_blank");
    },

    async batchEbayImport(ebayOrderId) {
      this.ebayBatchModal.visible = false;
      this.bulkLoading = true;
      this.bulkResult = null;

      const accessToken = localStorage.getItem("accessToken");
      let imported = 0;
      let failed = 0;

      for (const orderId of this.selectedOrders) {
        try {
          await this.api.importEbayOrder(
            { order_id: orderId, ebay_order_id: ebayOrderId },
            accessToken
          );
          imported++;
        } catch {
          failed++;
        }
      }

      this.bulkResult = `${imported} imported, ${failed} failed`;
      this.bulkLoading = false;
      if (imported > 0) {
        await this.fetchOrders();
      }
    },

    // Format timestamp for display
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString();
    },

    // Persist filter state to URL query params
    updateUrlQueryParams() {
      const query = {};
      if (this.statusFilter) query.status = this.statusFilter;
      if (this.deviceFilter) query.device = this.deviceFilter;
      if (this.serialSearch.trim()) query.serial = this.serialSearch.trim();
      if (this.shippingStatusFilter) query.shipping_status = this.shippingStatusFilter;

      const currentQuery = this.$route.query;
      const hasChanged =
        query.status !== currentQuery.status ||
        query.device !== currentQuery.device ||
        query.serial !== currentQuery.serial ||
        query.shipping_status !== currentQuery.shipping_status;

      if (hasChanged) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    restoreFiltersFromUrl() {
      const query = this.$route.query;
      if (query.status) this.statusFilter = query.status;
      if (query.device) this.deviceFilter = query.device;
      if (query.serial) this.serialSearch = query.serial;
      if (query.shipping_status) this.shippingStatusFilter = query.shipping_status;
    },
  },
};
</script>
